import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiErrorMessages from "../components/UI/ApiErrorMessages";
import BackButton from "../components/UI/BackButton";
import Button from "../components/UI/Button";
import CurrentPaymentDetails from "../components/UI/CurrentPaymentDetails";
import CurrentPlan from "../components/UI/CurrentPlan";
import NewPlan from "../components/UI/NewPlan";
import Modal from "../components/UI/Modal";
import { OrganizationPlan } from "../models/serviceModels/OrganizationPlan";
import { PaymentMethod } from "../models/serviceModels/PaymentMethod";
import { getItem, removeItem } from "../services/cacheService";
import { checkout } from "../services/paymentsService";
import Loader from "../components/UI/Loader";
import { INVOICED } from "../constants/billingType";
import InvoicedPaymentDetails from "../components/UI/InvoicedPaymentDetails";
import { MeContext, OrganizationContext } from "../App";
import { FREE_PLAN_TYPE_ID, STANDARD_PLAN_TYPE_ID, TRIAL_PLAN_TYPE_ID } from "../constants/planTypes";

const ConfirmPlanChange = () => {
    const [currentPlan, setCurrentPlan] = useState<OrganizationPlan>();
    const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>();
    const [checkoutApiErrors, setCheckoutApiErrors] = useState<any>();
    const [submitting, setSubmitting] = useState(false);
    const [showChargeConfirmation, setShowChargeConfirmation] = useState(false);
    const me = useContext(MeContext);
    const organization = useContext(OrganizationContext);

    const navigate = useNavigate();

    const newPlan = useMemo(() => {
        return getItem<{ users: number, plan: number }>('new_plan')
    }, []);

    useEffect(() => {
        if (organization.loading) {
            return;
        }

        if (organization.error) {
            setCheckoutApiErrors(organization.error);
        }
    }, [organization])

    const handleCheckout = () => {
        if (!newPlan) return;
        if (!currentPlan) return;

        setSubmitting(true);
        setCheckoutApiErrors(undefined);

        checkout({
            planTypeId: newPlan.plan
        }).then(() => {
            removeItem('new_plan');
            me.refresh();
            organization.refresh();
            navigate('/admin/billing');
        }).catch(e => {
            console.error(e);
            setCheckoutApiErrors(e);
        }).finally(() => {
            setSubmitting(false);
        });
    };

    const enableCheckout = () => {
        if (submitting || !newPlan || !currentPlan) {
            return false;
        }

        if (newPlan.plan === STANDARD_PLAN_TYPE_ID) {
            if (!paymentMethod && organization.value?.billingTypeId !== INVOICED) {
                return false;
            }
        }         
        
        return true;
    }

    const requiresStandardChargeConfirmation = !!currentPlan &&
        !!newPlan &&
        currentPlan.planTypeId !== STANDARD_PLAN_TYPE_ID &&
        newPlan.plan === STANDARD_PLAN_TYPE_ID &&
        organization.value?.billingTypeId !== INVOICED;

    const handleCheckoutClick = () => {
        if (requiresStandardChargeConfirmation) {
            setShowChargeConfirmation(true);
            return;
        }

        handleCheckout();
    };

    const handleCancelChargeConfirmation = () => {
        if (submitting) {
            return;
        }

        setShowChargeConfirmation(false);
    };

    const handleConfirmCharge = () => {
        setShowChargeConfirmation(false);
        handleCheckout();
    };

    const getMainContent = () => {
        if (organization.loading) {
            return <Loader/>
        }

        return (<section id="main-content" tabIndex={-1}>
            <BackButton />            
            <ApiErrorMessages error={checkoutApiErrors} />
            {organization.value && <div className="row justify-content-center margin-vertical-2">
                <div className="column-medium">
                    <div className="row">
                        <div className="column-medium-4">
                            <CurrentPlan onLoad={setCurrentPlan} />
                        </div>
                        <div className="column-medium-4">
                            <NewPlan />
                        </div>
                    </div>
                    <hr />
                    <div className="row margin-top-3">
                        <div className="column-medium-4">
                            {organization.value.billingTypeId !== INVOICED && <CurrentPaymentDetails onLoad={setPaymentMethod} />}
                            {organization.value.billingTypeId === INVOICED && <InvoicedPaymentDetails />}
                        </div>
                    </div>
                    <hr />
                    <div className="row">
                        <div className="column">
                            If everything looks good, then select <strong>checkout</strong>.
                        </div>
                    </div>
                    {newPlan?.plan !== TRIAL_PLAN_TYPE_ID && newPlan?.plan !== FREE_PLAN_TYPE_ID && <div className="row">
                        <div className="column-auto text-bold margin-vertical-3">
                            You may receive an email from Stripe, our payment processor, if additional verification is required to complete your payment.
                        </div>
                    </div>}
                    <div className="row justify-content-end">
                        <div className="column-auto">
                            <Button id="checkout-button" text="Checkout" className="action"
                                disabled={!enableCheckout()} loading={submitting}
                                onClick={handleCheckoutClick} />
                        </div>
                    </div>
                </div>
            </div>}
            <Modal id="confirm-standard-plan-charge-modal"
                visible={showChargeConfirmation}
                className="small"
                onClose={handleCancelChargeConfirmation}
                title="Confirm Submission and Charge">
                <div>
                    <div className="row">
                        <div className="column">
                            <p>You have selected the Standard Plan and added a credit card.</p>
                            <p>Your card will be <strong>charged immediately</strong> if you confirm.</p>
                        </div>
                    </div>
                    <div className="row margin-top-5">
                        <div className="column">
                            <div className="row justify-content-between">
                                <div className="column-medium-auto">
                                    <Button id="cancel-standard-plan-charge"
                                        text="Cancel"
                                        className="secondary"
                                        disabled={submitting}
                                        onClick={handleCancelChargeConfirmation} />
                                </div>
                                <div className="column-medium-auto">
                                    <Button id="confirm-standard-plan-charge"
                                        text="Confirm"
                                        className="action"
                                        loading={submitting}
                                        disabled={submitting}
                                        onClick={handleConfirmCharge} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>
        </section>)
    };

    return getMainContent();
};

export default ConfirmPlanChange;
